import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PostForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function PostForm({ post, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: '',
    tags: '',
    status: 'draft',
    image_url: '',
    published_date: '',
    media_type: 'image'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadMethod, setUploadMethod] = useState('upload'); // 'upload' or 'url'
  const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (post) {
      // Format the date for the input field (YYYY-MM-DD)
      let formattedDate = '';
      if (post.published_date) {
        const date = new Date(post.published_date);
        // Use local date to avoid timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }
      
      setFormData({
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        author: post.author || '',
        category: post.category || '',
        tags: post.tags || '',
        status: post.status || 'draft',
        image_url: post.image_url || '',
        published_date: formattedDate,
        media_type: post.media_type || 'image'
      });
      setMediaType(post.media_type || 'image');
      if (post.image_url) {
        // Check if it's an uploaded image (starts with 'images/blog/') or external URL
        if (post.image_url.startsWith('images/blog/')) {
          // It's an uploaded image - use API URL
          setImagePreview(`https://api.kanivuekm.com/${post.image_url}`);
          setUploadMethod('upload');
        } else if (post.image_url.startsWith('http')) {
          // It's an external URL
          setImagePreview(post.image_url);
          setUploadMethod('url');
        } else {
          // Fallback - treat as uploaded image
          setImagePreview(`https://api.kanivuekm.com/${post.image_url}`);
          setUploadMethod('upload');
        }
      }
    }
  }, [post]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type based on media type
      if (mediaType === 'image' && !file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (mediaType === 'video' && !file.type.startsWith('video/')) {
        setError('Please select a video file');
        return;
      }
      
      // Validate file size (5MB for images, 50MB for videos)
      const maxSize = mediaType === 'video' ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`${mediaType === 'video' ? 'Video' : 'Image'} size should be less than ${mediaType === 'video' ? '50MB' : '5MB'}`);
        return;
      }
      
      setImageFile(file);
      setError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData({
      ...formData,
      image_url: url
    });
    if (url) {
      setImagePreview(url);
    } else {
      setImagePreview('');
    }
  };

  const handleUploadMethodChange = (method) => {
    setUploadMethod(method);
    setImageFile(null);
    setImagePreview('');
    setFormData({
      ...formData,
      image_url: ''
    });
    setError('');
  };

  const handleMediaTypeChange = (type) => {
    setMediaType(type);
    setImageFile(null);
    setImagePreview('');
    setFormData({
      ...formData,
      image_url: '',
      media_type: type
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Create FormData for multipart/form-data
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('excerpt', formData.excerpt);
      submitData.append('content', formData.content);
      submitData.append('author', formData.author);
      submitData.append('category', formData.category);
      submitData.append('tags', formData.tags);
      submitData.append('status', formData.status);
      
      // Add published_date if provided
      if (formData.published_date) {
        submitData.append('published_date', formData.published_date);
      }
      
      // Add image/video based on upload method
      if (uploadMethod === 'upload' && imageFile) {
        submitData.append('image', imageFile);
      } else if (uploadMethod === 'url' && formData.image_url) {
        submitData.append('image_url', formData.image_url);
      }

      let response;
      if (post) {
        // Update existing post
        response = await axios.put(`${API_URL}/posts/${post.id}`, submitData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000, // 2 minutes timeout for large video files
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            console.log(`Upload Progress: ${percentCompleted}%`);
          }
        });
      } else {
        // Create new post
        response = await axios.post(`${API_URL}/posts`, submitData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000, // 2 minutes timeout for large video files
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload Progress: ${percentCompleted}%`);
          }
        });
      }

      // Check if response is successful (status 200-299)
      if (response.status >= 200 && response.status < 300) {
        setUploadProgress(0);
        onSuccess();
      } else {
        setError('Unexpected response from server');
      }
    } catch (err) {
      console.error('Error saving post:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Upload timeout - file may be too large. Please try a smaller video or use a URL instead.');
      } else if (err.message === 'Network Error') {
        setError('Network error - please check your connection and try again. For large videos, consider using a URL instead.');
      } else {
        setError(err.response?.data?.message || err.message || 'Error saving post');
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="post-form">
      <h2>{post ? 'Edit Post' : 'Create New Post'}</h2>
      {error && <div className="error-message">{error}</div>}
      {loading && uploadProgress > 0 && (
        <div style={{ 
          padding: '10px', 
          marginBottom: '15px', 
          backgroundColor: '#e3f2fd', 
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '5px' }}>Uploading... {uploadProgress}%</div>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: '#ddd', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${uploadProgress}%`, 
              height: '100%', 
              backgroundColor: '#2196f3',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="excerpt">Excerpt</label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            rows="3"
            placeholder="Brief summary of the post (optional)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="content">Content *</label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            rows="10"
            required
          />
        </div>

        <div className="form-group">
          <label>Featured Media</label>
          <div className="media-type-selector" style={{ marginBottom: '15px' }}>
            <button
              type="button"
              className={mediaType === 'image' ? 'active' : ''}
              onClick={() => handleMediaTypeChange('image')}
              style={{ marginRight: '10px' }}
            >
              Image
            </button>
            <button
              type="button"
              className={mediaType === 'video' ? 'active' : ''}
              onClick={() => handleMediaTypeChange('video')}
            >
              Video
            </button>
          </div>
          
          <div className="image-upload-method">
            <button
              type="button"
              className={uploadMethod === 'upload' ? 'active' : ''}
              onClick={() => handleUploadMethodChange('upload')}
            >
              Upload from PC
            </button>
            <button
              type="button"
              className={uploadMethod === 'url' ? 'active' : ''}
              onClick={() => handleUploadMethodChange('url')}
            >
              Use URL
            </button>
          </div>

          {uploadMethod === 'upload' ? (
            <div className="image-upload-section">
              <input
                type="file"
                id="image-file"
                accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                onChange={handleImageFileChange}
              />
              <label htmlFor="image-file" className="file-upload-label">
                {imageFile ? imageFile.name : `Choose ${mediaType === 'image' ? 'an image file (max 5MB)' : 'a video file (max 50MB)'}`}
              </label>
              {mediaType === 'video' && (
                <small style={{ display: 'block', marginTop: '8px', color: '#666', fontSize: '12px' }}>
                  💡 Tip: For videos larger than 50MB, use "Use URL" option with a direct video link (e.g., from Google Drive, Dropbox, or your own server)
                </small>
              )}
            </div>
          ) : (
            <input
              type="url"
              id="image_url"
              name="image_url"
              value={formData.image_url}
              onChange={handleImageUrlChange}
              placeholder={mediaType === 'image' ? 'https://example.com/image.jpg' : 'https://example.com/video.mp4'}
            />
          )}

          {imagePreview && (
            <div className="image-preview">
              {mediaType === 'image' ? (
                <img src={imagePreview} alt="Preview" />
              ) : (
                <video controls style={{ maxWidth: '100%', maxHeight: '400px' }}>
                  <source src={imagePreview} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="author">Author</label>
            <input
              type="text"
              id="author"
              name="author"
              value={formData.author}
              onChange={handleChange}
              placeholder="Author name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              placeholder="e.g., Technology, Health"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="tags">Tags</label>
          <input
            type="text"
            id="tags"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="Comma-separated tags (e.g., web, design, tutorial)"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="published_date">Published Date</label>
            <input
              type="date"
              id="published_date"
              name="published_date"
              value={formData.published_date}
              onChange={handleChange}
              placeholder="Select publication date"
            />
            <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
              Leave empty to use current date when publishing
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Saving...' : (post ? 'Update Post' : 'Create Post')}
          </button>
          <button type="button" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default PostForm;